import React, { useEffect, useState } from 'react'
import { Form, Row, Col, Card, Typography, message, Spin } from 'antd'
import { useNavigate, useParams } from 'react-router-dom';
import { goBack } from 'assets/icons';
import styled from '@emotion/styled';
import CreateCampaignFooter from '../CreateCampaign/CreateProductFooter';
import MainCreateCampaign from '../CreateCampaign/MainCreateCampaign';
import { useGetCampaignById, useUpdateCampaign } from 'hooks/useCampaign';
import { UpdateCampaignInput } from 'services/interface';
import { useAppSelector } from 'store';
import dayjs from 'dayjs';

const UpdateCampaign = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { data: campaignData, isLoading: isLoadingCampaign } = useGetCampaignById(id || '');
    const { mutateAsync: updateCampaign, isPending } = useUpdateCampaign();
    const contact = useAppSelector((state) => state.auth.contact);
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [pendingFreeGifts, setPendingFreeGifts] = useState<any[] | null>(null);
    const [componentKey, setComponentKey] = useState(0);

    // Effect แรก: โหลดข้อมูลพื้นฐานและ conditions
    useEffect(() => {
        if (campaignData?.data && !isDataLoaded) {
            const campaign = campaignData.data;
            
            // หาค่า reward_type
            const firstReward = campaign.rewards?.[0];
            let resultType = 'discount';
            let discountUnit = 'amount';
            let discountValue = 0;

            if (firstReward) {
                if (firstReward.reward_type === 'free_product') {
                    resultType = 'free_product';
                } else if (firstReward.reward_type === 'discount_percentage') {
                    resultType = 'discount';
                    discountUnit = 'percentage';
                    discountValue = firstReward.discount_percentage || 0;
                } else if (firstReward.reward_type === 'discount_amount') {
                    resultType = 'discount';
                    discountUnit = 'amount';
                    discountValue = firstReward.discount_amount || 0;
                }
            }

            // แปลง conditions
            const firstCondition = campaign.conditions?.[0];
            const conditions = firstCondition?.products?.map((p) => ({
                // Normalize ids to string so Select can match options reliably
                product_id:
                    (p as any)?.product_id != null
                        ? String((p as any).product_id)
                        : (p as any)?.product?.id != null
                            ? String((p as any).product.id)
                            : undefined,
                // Some APIs may return variant id nested in `sku_variant`
                sku_variant_id:
                    (p as any)?.sku_variant_id != null
                        ? String((p as any).sku_variant_id)
                        : (p as any)?.sku_variant?.id != null
                            ? String((p as any).sku_variant.id)
                            : (p as any)?.sku_variant?.item_id != null
                                ? String((p as any).sku_variant.item_id)
                                : (p as any)?.sku_variant?.variant_item_id != null
                                    ? String((p as any).sku_variant.variant_item_id)
                                    : undefined,
                quantity: p.required_quantity,
                decision: '+',
            })) || [];

            // แปลง free_gifts
            const freeGifts = firstReward?.products?.map((p) => ({
                product_id: p.product_id,
                sku_variant_id: p.sku_variant_id,
                quantity: p.quantity,
                decision: 'AND',
            })) || [];

            // Set ค่าพื้นฐานทั้งหมด
            form.setFieldsValue({
                campaign_name: campaign.title,
                description: campaign.description,
                start_date: campaign.start_date ? dayjs(campaign.start_date) : null,
                expire_date: campaign.end_date ? dayjs(campaign.end_date) : null,
                is_disabled: !campaign.is_active,
                campaign_type: campaign.campaign_type,
                max_usage: campaign.max_usage,
                max_usage_per_user: campaign.max_usage_per_user,
                remark: campaign.remark,
                condition_type: firstCondition?.condition_type,
                conditions: conditions.length > 0 ? conditions : [{ quantity: 1, decision: '+' }],
                result_type: resultType,
                discount_unit: discountUnit,
                discount_value: discountValue,
            });

            // เก็บ freeGifts ไว้ set ทีหลังหลัง result_type render แล้ว
            if (resultType === 'free_product' && freeGifts.length > 0) {
                setPendingFreeGifts(freeGifts);
            }

            setIsDataLoaded(true);
        }
    }, [campaignData, form, isDataLoaded]);

    // Effect ที่สอง: set free_gifts หลังจาก result_type render แล้ว
    useEffect(() => {
        if (pendingFreeGifts && pendingFreeGifts.length > 0) {
            // รอให้ form render result_type และ Form.List ของ free_gifts ถูกสร้างก่อน
            const timer1 = setTimeout(() => {
                // บังคับให้ component re-render
                setComponentKey(prev => prev + 1);
            }, 100);

            const timer2 = setTimeout(() => {
                form.setFieldValue('free_gifts', pendingFreeGifts);
            }, 500);

            return () => {
                clearTimeout(timer1);
                clearTimeout(timer2);
            };
        }
    }, [pendingFreeGifts, form]);

    const onFinish = async (values: any) => {
        if (!id) {
            message.error('ไม่พบ ID แคมเปญ');
            return;
        }

        try {
            const startDateIso = values.start_date ? new Date(values.start_date).toISOString() : null;
            const endDateIso = values.expire_date ? new Date(values.expire_date).toISOString() : null;

            const payload: UpdateCampaignInput = {
                id: id,
                title: values.campaign_name,
                description: values.description ?? null,
                campaign_type: values.campaign_type,
                status: "active", // เป็น active เสมอ
                start_date: startDateIso as any,
                end_date: endDateIso,
                is_active: !values.is_disabled,
                factory_id: contact?.id || null, // เป็น factory ของฉันเสมอ
                max_usage: Number(values.max_usage ?? 0),
                max_usage_per_user: Number(values.max_usage_per_user ?? 1),
                remark: values.remark ?? null,
                conditions: [
                    {
                        condition_type: values.condition_type,
                        priority: 1,
                        products: (values.conditions ?? []).map((row: any, index: number) => ({
                            product_id: row.product_id,
                            sku_variant_id: row.sku_variant_id,
                            required_quantity: Number(row.quantity ?? 1),
                            priority: index + 1,
                        })),
                    },
                ],
                rewards: [],
            };

            if (values.result_type === 'free_product') {
                payload.rewards = [
                    {
                        reward_type: 'free_product',
                        quantity: 1,
                        priority: 1,
                        products: (values.free_gifts ?? []).map((row: any, index: number) => ({
                            product_id: row.product_id,
                            sku_variant_id: row.sku_variant_id,
                            quantity: Number(row.quantity ?? 1),
                            priority: index + 1,
                        })),
                    },
                ];
            } else {
                const unit = values.discount_unit === 'percentage' ? 'discount_percentage' : 'discount_amount';
                payload.rewards = [
                    {
                        reward_type: unit,
                        quantity: 1,
                        priority: 1,
                        discount_percentage: unit === 'discount_percentage' ? Number(values.discount_value ?? 0) : null,
                        discount_amount: unit === 'discount_amount' ? Number(values.discount_value ?? 0) : null,
                    },
                ];
            }

            await updateCampaign(payload);
            message.success('อัพเดทแคมเปญสำเร็จ');
            navigate('/campaign');
        } catch (error: any) {
            message.error(error?.message || 'อัพเดทแคมเปญไม่สำเร็จ');
        }
    }

    if (isLoadingCampaign) {
        return (
            <Row className="rowgap-vbox" gutter={[24, 0]}>
                <Col span={24}>
                    <Card bordered={false} className="criclebox h-full tabs-mobile">
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                            <Spin size="large" />
                        </div>
                    </Card>
                </Col>
            </Row>
        );
    }

    return (
        <Form form={form} onFinish={onFinish}>
            <Row className="rowgap-vbox" gutter={[24, 0]}>
                <Col span={24}>
                    <Card bordered={false} className="criclebox h-full tabs-mobile">
                        <HeaderTabs>
                            <IconWrapper
                                onClick={() => {
                                    navigate("/campaign");
                                }}
                            >
                                <SpanIcon>{goBack}</SpanIcon>
                            </IconWrapper>
                            <Typography.Title level={5}>แก้ไขแคมเปญ</Typography.Title>
                            <RightIconWrapper></RightIconWrapper>
                        </HeaderTabs>
                        <WrapperAll>
                            <MainCreateCampaign
                                key={componentKey}
                                form={form}
                                onFinish={onFinish}
                            />
                        </WrapperAll>
                    </Card>
                </Col>
            </Row>
            <CreateCampaignFooter loading={isPending} />
        </Form>
    )
}

export default UpdateCampaign

const HeaderTabs = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  flex-direction: row;
  border-bottom: 1px solid #efefef;
`;

const IconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 30px;
  border-radius: 6px;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  &:hover {
    background-color: #efefef;
  }
`;

const SpanIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const RightIconWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const WrapperAll = styled.div`
  display: block;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
`;
